import axios from 'axios'

// Separate client for admin with cookie auth
const adminClient = axios.create({
  baseURL: '/api/admin',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important for cookies
})

// Types
export interface Admin {
  id: number
  username: string
  role: string
  is_active: boolean
  last_login_at: string | null
  created_at: string
}

export interface Stats {
  total_users: number
  total_projects: number
  projects_completed: number
  projects_processing: number
  projects_failed: number
}

export interface Pipeline {
  id: number
  name: string
  description: string | null
  is_active: boolean
  created_at: string
}

export interface PipelineStage {
  id: number
  name: string
  stage_type: string
  model: string | null
  prompt_template: string | null
  temperature: number | null
  max_tokens: number | null
  queue: string
  is_active: boolean
  description: string | null
}

export interface PromptVersion {
  id: number
  stage_id: number
  version: number
  prompt_template: string
  change_description: string | null
  is_active: boolean
  created_at: string
}

export interface OpenRouterModel {
  id: string
  name: string
  description: string | null
  context_length: number
  pricing_prompt: number
  pricing_completion: number
  supports_functions: boolean
  supports_vision: boolean
  is_available: boolean
}

export interface Project {
  id: number
  user_id: number
  title: string | null
  source_url: string
  status: string
  current_stage_index: number
  created_at: string
  updated_at: string
}

// Auth
export const login = async (username: string, password: string): Promise<{ success: boolean; admin: Admin }> => {
  const response = await adminClient.post('/login', { username, password })
  return response.data
}

export const logout = async (): Promise<void> => {
  await adminClient.post('/logout')
}

export const getMe = async (): Promise<Admin> => {
  const response = await adminClient.get('/me')
  return response.data
}

// Stats
export const getStats = async (): Promise<Stats> => {
  const response = await adminClient.get('/stats')
  return response.data
}

// Pipelines
export const getPipelines = async (): Promise<Pipeline[]> => {
  const response = await adminClient.get('/pipelines')
  return response.data
}

export const getStages = async (): Promise<PipelineStage[]> => {
  const response = await adminClient.get('/stages')
  return response.data
}

export const getStage = async (stageId: number): Promise<PipelineStage> => {
  const response = await adminClient.get(`/stages/${stageId}`)
  return response.data
}

export const updateStage = async (
  stageId: number,
  data: Partial<Pick<PipelineStage, 'name' | 'model' | 'prompt_template' | 'temperature' | 'max_tokens' | 'is_active'>>
): Promise<PipelineStage> => {
  const response = await adminClient.patch(`/stages/${stageId}`, data)
  return response.data
}

// Prompts
export const getPromptVersions = async (stageId: number): Promise<PromptVersion[]> => {
  const response = await adminClient.get(`/stages/${stageId}/prompts`)
  return response.data
}

export const createPromptVersion = async (
  stageId: number,
  data: { prompt_template: string; change_description?: string; activate?: boolean }
): Promise<PromptVersion> => {
  const response = await adminClient.post(`/stages/${stageId}/prompts`, data)
  return response.data
}

export const activatePromptVersion = async (stageId: number, version: number): Promise<void> => {
  await adminClient.post(`/stages/${stageId}/prompts/${version}/activate`)
}

// Models
export const getModels = async (): Promise<OpenRouterModel[]> => {
  const response = await adminClient.get('/models')
  return response.data
}

export const refreshModels = async (): Promise<{ success: boolean; count: number }> => {
  const response = await adminClient.post('/models/refresh')
  return response.data
}

// Projects
export const getProjects = async (params?: {
  limit?: number
  offset?: number
  status?: string
}): Promise<Project[]> => {
  const response = await adminClient.get('/projects', { params })
  return response.data
}

// Test pipeline
export const testPipeline = async (url: string, telegramId?: number): Promise<{
  project_id: number
  user_id: number
  status: string
  message: string
}> => {
  const response = await adminClient.post('/test-pipeline', {
    url,
    telegram_id: telegramId,
  })
  return response.data
}

export default adminClient
